import { useCallback, useEffect, useMemo, useState } from 'react';

import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  type FilterId,
  FilterType,
  type MultiFilter,
} from '../types/filters';

type MultiInstanceContext = {
  parentId: FilterId;
  instanceIndex?: number;
  isNew?: boolean;
};

export type MultiInstanceMeta = {
  parentMultiFilterId: FilterId;
  instanceIndex?: number;
  isNewMultiInstance?: boolean;
};

type SelectedFilterResult = {
  filter: Filter;
  meta?: MultiInstanceMeta;
};

export type UnpackedFilter = Filter & {
  displayId?: string;
  parentMultiFilterId?: FilterId;
  instanceIndex?: number;
};
interface UseMultiFilterEditorArgs {
  filters: Map<FilterId, Filter>;
  initialFilter?: UnpackedFilter;
  isOpen: boolean;
}
interface UseMultiFilterEditorReturn {
  selectedFilter: Filter | null;
  selectedFilterId: FilterId | null;
  selectedMultiMeta?: MultiInstanceMeta;
  selectFilter: (filterId: FilterId | null) => void;
  clearSelection: () => void;
}

export const useMultiFilterEditor = ({
  filters,
  initialFilter,
  isOpen,
}: UseMultiFilterEditorArgs): UseMultiFilterEditorReturn => {
  const [multiContext, setMultiContext] = useState<MultiInstanceContext | null>(
    null
  );
  const [autoOpenedParentId, setAutoOpenedParentId] = useState<FilterId | null>(
    null
  );
  const [selectedFilterId, setSelectedFilterId] = useState<FilterId | null>(
    null
  );

  const resolveMultiParent = useCallback(
    (parentId: FilterId): MultiFilter<Filter> | undefined => {
      const existing = filters.get(parentId);
      if (existing && existing.type === FilterType.MULTI) {
        return existing;
      }

      const definition = getFilterDefinition(parentId);
      if (definition && definition.type === FilterType.MULTI) {
        return {
          ...definition,
          instances: [...(definition.instances ?? [])],
        };
      }

      return undefined;
    },
    [filters]
  );

  const selectedFilterResult = useMemo<SelectedFilterResult | null>(() => {
    if (initialFilter) {
      const parentMultiFilterId = initialFilter.parentMultiFilterId;
      const instanceIndex = initialFilter.instanceIndex;

      if (parentMultiFilterId !== undefined && instanceIndex !== undefined) {
        const meta: MultiInstanceMeta = {
          parentMultiFilterId,
          instanceIndex,
        };

        return {
          filter: initialFilter,
          meta,
        };
      }

      return { filter: initialFilter };
    }

    if (multiContext) {
      const parent = resolveMultiParent(multiContext.parentId);
      if (!parent) {
        return null;
      }

      const wrappedDefinition = getFilterDefinition(parent.wrappedFilterId);

      const existingInstance =
        multiContext.instanceIndex !== undefined
          ? parent.instances[multiContext.instanceIndex]
          : undefined;

      let baseInstance: Filter | null = null;
      if (existingInstance) {
        baseInstance = { ...existingInstance } as Filter;
      } else if (wrappedDefinition) {
        baseInstance = { ...wrappedDefinition } as Filter;
      }

      if (!baseInstance) {
        return null;
      }

      const meta: MultiInstanceMeta = {
        parentMultiFilterId: parent.id,
        isNewMultiInstance:
          Boolean(multiContext.isNew) || existingInstance === undefined,
      };

      if (multiContext.instanceIndex !== undefined) {
        meta.instanceIndex = multiContext.instanceIndex;
      }

      return {
        filter: baseInstance,
        meta,
      };
    }

    if (!selectedFilterId) {
      return null;
    }

    const existingFilter = Array.from(filters.values()).find(
      (value) => value.id === selectedFilterId
    );

    // Check if the selected filter is a MULTI filter
    // If so, immediately resolve to the wrapped instance instead of returning the MULTI filter
    const filterDefinition = existingFilter
      ? null
      : getFilterDefinition(selectedFilterId);
    const filterToCheck = existingFilter ?? filterDefinition;

    if (filterToCheck && filterToCheck.type === FilterType.MULTI) {
      const multiFilter = filterToCheck as MultiFilter<Filter>;
      const parent = resolveMultiParent(multiFilter.id);
      if (!parent) {
        return null;
      }

      const wrappedDefinition = getFilterDefinition(parent.wrappedFilterId);
      if (!wrappedDefinition) {
        return null;
      }

      // Return the wrapped instance immediately, with meta indicating it's a new instance
      const baseInstance = { ...wrappedDefinition } as Filter;
      const meta: MultiInstanceMeta = {
        parentMultiFilterId: parent.id,
        isNewMultiInstance: true,
      };

      return {
        filter: baseInstance,
        meta,
      };
    }

    if (existingFilter) {
      return { filter: existingFilter };
    }

    return filterDefinition
      ? { filter: { ...filterDefinition } as Filter }
      : null;
  }, [
    filters,
    initialFilter,
    multiContext,
    resolveMultiParent,
    selectedFilterId,
  ]);

  const selectedFilter = selectedFilterResult?.filter ?? null;
  const selectedMultiMeta = selectedFilterResult?.meta;

  useEffect(() => {
    if (isOpen && !initialFilter && selectedFilterId) {
      // Check if selectedFilterId is a MULTI filter
      const filterDefinition = getFilterDefinition(selectedFilterId);
      if (filterDefinition && filterDefinition.type === FilterType.MULTI) {
        const multiFilter = filterDefinition as MultiFilter<Filter>;
        const alreadyAutoOpened = autoOpenedParentId === multiFilter.id;
        if (!multiContext && !alreadyAutoOpened) {
          setMultiContext({ parentId: multiFilter.id, isNew: true });
          setAutoOpenedParentId(multiFilter.id);
        }
      }
    }
  }, [
    autoOpenedParentId,
    initialFilter,
    isOpen,
    multiContext,
    selectedFilterId,
  ]);

  const resetMultiEditor = useCallback(() => {
    setMultiContext(null);
    setAutoOpenedParentId(null);
  }, []);

  const selectFilter = useCallback(
    (filterId: FilterId | null) => {
      resetMultiEditor();
      setSelectedFilterId(filterId);
    },
    [resetMultiEditor]
  );

  const clearSelection = useCallback(() => {
    resetMultiEditor();
    setSelectedFilterId(null);
  }, [resetMultiEditor]);

  return {
    selectedFilter,
    selectedFilterId,
    selectedMultiMeta,
    selectFilter,
    clearSelection,
  };
};
